import { FC, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MuiCheckbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';
import { LpInvestorUpdateType } from 'further-types/lp/investor-update';
import { downloadFileFromTrpc } from 'further-ui/utils';
import { Row, Column } from 'further-ui/components/Layout';
import SearchField from 'further-ui/components/SearchField';
import Avatar from 'further-ui/components/Avatar';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import RadioButtons from 'further-ui/components/RadioButtons';
import FullPageDrawer from 'components/new/FullPageDrawer';
import PageFilters, { Filter } from 'components/new/PageFilters';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';

const TableContainer = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
});

const Checkbox = styled(MuiCheckbox)({
  padding: 0,
});

enum DownloadMode {
  Pdf = 'pdf',
  Excel = 'excel',
  Both = 'both',
}

type Investor = {
  id: string;
  fullName: string;
};

type Props = {
  vehicleId: string;
  reportId: string;
  investorUpdate: {
    type: LpInvestorUpdateType;
    title: string;
    investorPdfUploads: Array<{
      investorId: string;
      path: string;
      previewUrl: string;
    }>;
    investors: Array<Investor>;
  } | null;
  onClose: () => void;
};

const DownloadInvestorUpdates: FC<Props> = ({
  vehicleId,
  reportId,
  investorUpdate,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvestorIds, setSelectedInvestorIds] = useState<Array<string>>(
    [],
  );
  const [downloadMode, setDownloadMode] = useState(DownloadMode.Pdf);
  const investors = investorUpdate?.investors ?? [];

  const { mutateAsync: downloadExcel, isPending: isExcelDownloading } =
    api.periodEndReport.downloadFinancialStatementsExcel.useMutation(
      useHandleTrpcMutation()({ invalidations: [] }),
    );

  const findInvestorPdfUpload = (investorId: string) =>
    investorUpdate?.investorPdfUploads.find(
      (upload) => upload.investorId === investorId,
    );

  const filteredInvestors = investors.filter((investor) =>
    investor.fullName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const selectableInvestors =
    downloadMode === DownloadMode.Pdf
      ? filteredInvestors.filter(
          (investor) => !!findInvestorPdfUpload(investor.id),
        )
      : filteredInvestors;

  const allVisibleInvestorsSelected =
    selectableInvestors.length > 0 &&
    selectableInvestors.every((investor) =>
      selectedInvestorIds.includes(investor.id),
    );

  const handleSelectDeselectAll = () => {
    setSelectedInvestorIds(
      allVisibleInvestorsSelected
        ? []
        : selectableInvestors.map((investor) => investor.id),
    );
  };

  const handleDownload = async () => {
    if (!investorUpdate) return;
    if (selectedInvestorIds.length === 0) return;

    const result = await downloadExcel({
      vehicleId,
      id: reportId,
      investorIds: selectedInvestorIds,
      includePdfs: downloadMode !== DownloadMode.Excel,
      includeExcel: downloadMode !== DownloadMode.Pdf,
    });
    downloadFileFromTrpc(result);
  };

  const columns: Array<TableColumn<Investor>> = [
    {
      key: 'investor',
      render: ({ fullName }) => (
        <Row spacing="xs" centered>
          <Avatar color="random" text={fullName} size="lg" />
          <Typography variant="h6">{fullName}</Typography>
        </Row>
      ),
    },
    {
      key: 'actions',
      width: '50px',
      render: ({ id }) => {
        const isInvestorWithoutPdfUpload =
          downloadMode === DownloadMode.Pdf && !findInvestorPdfUpload(id);
        const tooltipTitle = isInvestorWithoutPdfUpload
          ? 'This PDF is currently being generated. Please try again in a few moments.'
          : undefined;

        return (
          <Tooltip title={tooltipTitle} placement="bottom-start">
            <span>
              <Checkbox
                checked={selectedInvestorIds.includes(id)}
                disabled={isInvestorWithoutPdfUpload}
                onChange={(e) => {
                  setSelectedInvestorIds((prev) =>
                    e.target.checked
                      ? [...prev, id]
                      : prev.filter((prevId) => prevId !== id),
                  );
                }}
              />
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <FullPageDrawer
      open={true}
      title="Download period-end reports"
      onClose={onClose}
      innerScroll
      actions={
        <Row spacing="sm" justify="space-between">
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            loading={isExcelDownloading}
            onClick={handleDownload}
            disabled={selectedInvestorIds.length === 0}
          >
            Download
          </Button>
        </Row>
      }
    >
      <Column spacing="lg" fullWidth fullHeight>
        <Typography variant="body2">
          Please select one or more investors to download period-end reports
          from.
        </Typography>
        <RadioButtons
          direction="row"
          value={downloadMode}
          onChange={(value) => {
            setDownloadMode(value as DownloadMode);
            setSelectedInvestorIds([]);
          }}
          options={[
            { value: DownloadMode.Pdf, label: 'Investor reports' },
            { value: DownloadMode.Excel, label: 'Statement spreadsheets' },
            { value: DownloadMode.Both, label: 'Both' },
          ]}
        />
        <PageFilters
          justify="space-between"
          actions={
            selectableInvestors.length > 0
              ? [
                  {
                    label: allVisibleInvestorsSelected
                      ? 'Deselect all'
                      : 'Select all',
                    onClick: handleSelectDeselectAll,
                  },
                ]
              : []
          }
        >
          <Filter size="md">
            <SearchField
              fullWidth
              placeholder="Search by investor"
              value={searchQuery}
              onSearch={setSearchQuery}
              allowEmptyQuery
              autoSearchOnDebounce
              autoSearchOnBlur
              debounceTime={1}
            />
          </Filter>
        </PageFilters>
        <TableContainer>
          <Table
            columns={columns}
            rows={filteredInvestors}
            emptyMessage="No investors found."
          />
        </TableContainer>
      </Column>
    </FullPageDrawer>
  );
};

export default DownloadInvestorUpdates;
